import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useChat } from '../../contexts/ChatContext';
import styles from './MinimizedChatBubble.module.scss';

/**
 * MinimizedChatBubble
 *
 * Floating notification card that appears at the bottom-right when a
 * conversation is minimized. Styled to match the app’s clean white UI.
 * Rendered via ReactDOM.createPortal so it persists across route changes.
 */
const MinimizedChatBubble = () => {
  const { chatState, restoreChat, closeChat } = useChat();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (chatState.isMinimized) {
      const t = setTimeout(() => setMounted(true), 10);
      return () => clearTimeout(t);
    } else {
      setMounted(false);
    }
  }, [chatState.isMinimized]);

  if (!chatState.isMinimized || !mounted) return null;

  const isLive        = chatState.isOngoingSession || chatState.isJoining;
  const isFlaggedChat = chatState.modalType === 'conversation' && !isLive;
  const displayName   = chatState.displayName || (isLive ? 'Live Session' : 'Active Chat');
  const displayPhone  = chatState.displayPhone;
  const unread        = chatState.unreadCount || 0;

  const handleExpand = () => restoreChat();
  const handleClose  = (e) => { e.stopPropagation(); closeChat(); };

  return ReactDOM.createPortal(
    <div className={styles.bubble} role="complementary" aria-label="Minimized chat window">
      {/* Colour accent strip on the left edge */}
      <div className={`${styles.accent}${isLive ? ` ${styles.live}` : ''}`} />

      {/* Card body */}
      <div className={styles.card}>
        {/* Row 1: avatar + info + action buttons */}
        <div className={styles.row}>
          {/* Avatar */}
          <div className={styles.avatarWrap}>
            <div className={`${styles.avatar}${isLive ? ` ${styles.live}` : ''}`}>
              <i className={`fa ${isFlaggedChat ? 'fa-flag' : 'fa-comments'}`} />
            </div>
            {/* Online dot */}
            <span className={`${styles.dot}${isLive ? ` ${styles.live}` : ''}`} />
            {/* Unread badge */}
            {unread > 0 && (
              <span className={styles.unreadBadge}>{unread > 9 ? '9+' : unread}</span>
            )}
          </div>

          {/* Name + subtext */}
          <div className={styles.info} onClick={handleExpand} role="button" tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && handleExpand()}>
            <div className={styles.name}>{displayName}</div>
            <div className={styles.subtext}>
              {isLive && <span className={styles.liveDot} />}
              {unread > 0
                ? `${unread} new message${unread > 1 ? 's' : ''}`
                : isLive ? 'Live • Tap to resume' : isFlaggedChat ? 'Flagged • Tap to open' : 'Minimized • Tap to open'}
            </div>
          </div>

          {/* Action buttons - side by side, no overlap */}
          <div className={styles.actions}>
            <button
              className={styles.actionBtn}
              onClick={handleExpand}
              aria-label="Restore conversation"
              title="Open conversation"
            >
              <i className="fa fa-expand" />
            </button>
            <button
              className={`${styles.actionBtn} ${styles.closeBtn}`}
              onClick={handleClose}
              aria-label="Close conversation"
              title="Close conversation"
            >
              <i className="fa fa-times" />
            </button>
          </div>
        </div>

        {/* Row 2: footer info */}
        {displayPhone && (
          <div className={styles.footer} onClick={handleExpand} style={{ cursor: 'pointer' }}>
            <span className={styles.footerTag}>
              <i className="fa fa-phone" />
              {isLive ? 'Live chat' : isFlaggedChat ? 'Flagged chat' : 'Active chat'}
            </span>
            <span className={styles.footerPhone}>{displayPhone}</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MinimizedChatBubble;
